import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tags } from 'lucide-react';
import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input, Select } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import { queryKeys } from '@/lib/query-keys.js';
import { dateOnly } from '@/lib/format.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { createContentApi, type ContentRecord } from '../content-api.js';

export type NewsRecord = ContentRecord & {
  title: TranslatableString;
  excerpt: TranslatableString;
  body: TranslatableString;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  youtubeUrl: string;
  categoryId: string | null;
  tags: string[];
  isFeatured: boolean;
  seo: SeoMeta;
};

export type NewsCategoryRecord = ContentRecord & { name: TranslatableString };

export const newsApi = createContentApi<NewsRecord>('/news');
export const newsCategoriesApi = createContentApi<NewsCategoryRecord>('/news-categories');

type NewsForm = {
  title: TranslatableString;
  slug: string;
  excerpt: TranslatableString;
  body: TranslatableString;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  youtubeUrl: string;
  categoryId: string;
  tags: string;
  isFeatured: boolean;
  seo: SeoMeta;
};

function useCategoryOptions() {
  const params = { limit: 100, sort: 'title' as const };
  const query = useQuery({
    queryKey: queryKeys.content.list('news-categories', params),
    queryFn: ({ signal }) => newsCategoriesApi.list(params, signal),
    staleTime: 5 * 60 * 1000,
  });

  return (query.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.name?.uz || row.slug || row._id,
  }));
}

// ─── Ro'yxat ─────────────────────────────────────────────────────────────────

export function NewsListPage() {
  const categories = useCategoryOptions();
  const byId = new Map(categories.map((option) => [option.value, option.label]));

  return (
    <ContentListPage<NewsRecord>
      title="Yangiliklar"
      collection="news"
      contentApi={newsApi}
      basePath="/news"
      createLabel="Yangilik qo‘shish"
      emptyTitle="Hozircha yangilik yo‘q"
      emptyHint="Birinchi yangilikni qo‘shing — u bosh sahifadagi «Yangiliklar» blokida ham ko‘rinadi."
      actions={
        <Link to="/news-categories">
          <Button variant="outline" icon={<Tags className="size-4" />}>
            Kategoriyalar
          </Button>
        </Link>
      }
      columns={[
        {
          key: 'title',
          header: 'Sarlavha',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'category',
          header: 'Kategoriya',
          width: '180px',
          hideOnTablet: true,
          render: (row) => (
            <span className="text-n-600">
              {row.categoryId ? (byId.get(row.categoryId) ?? '—') : '—'}
            </span>
          ),
        },
        {
          key: 'publishedAt',
          header: 'Nashr sanasi',
          width: '130px',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{dateOnly(row.publishedAt)}</span>,
        },
      ]}
    />
  );
}

// ─── Tahrirlash ──────────────────────────────────────────────────────────────

export function NewsEditPage() {
  const categories = useCategoryOptions();

  return (
    <ContentEditPage<NewsForm>
      collection="news"
      contentApi={newsApi}
      basePath="/news"
      backLabel="Yangiliklar"
      newTitle="Yangi yangilik"
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: { uz: '', ru: '', en: '' },
        slug: '',
        excerpt: { uz: '', ru: '', en: '' },
        body: { uz: '', ru: '', en: '' },
        coverImage: null,
        gallery: [],
        youtubeUrl: '',
        categoryId: '',
        tags: '',
        isFeatured: false,
        seo: emptySeo(),
      })}
      fromRecord={(record) => {
        const news = record as NewsRecord;
        return {
          title: news.title ?? { uz: '', ru: '', en: '' },
          slug: news.slug ?? '',
          excerpt: news.excerpt ?? { uz: '', ru: '', en: '' },
          body: news.body ?? { uz: '', ru: '', en: '' },
          coverImage: news.coverImage ?? null,
          gallery: news.gallery ?? [],
          youtubeUrl: news.youtubeUrl ?? '',
          categoryId: news.categoryId ?? '',
          tags: (news.tags ?? []).join(', '),
          isFeatured: news.isFeatured ?? false,
          seo: news.seo ?? emptySeo(),
        };
      }}
      toInput={(form) => ({
        title: form.title,
        slug: form.slug,
        excerpt: form.excerpt,
        body: form.body,
        coverImage: form.coverImage,
        gallery: form.gallery,
        youtubeUrl: form.youtubeUrl.trim(),
        categoryId: form.categoryId || null,
        // Teglar vergul bilan kiritiladi — bo'shlari tashlanadi, aks holda
        // «dron, , shou» uchta teg bo'lib qolardi.
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        isFeatured: form.isFeatured,
        seo: form.seo,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Matn" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Sarlavha"
                  required
                  value={form.title}
                  onChange={(title) => setForm((current) => ({ ...current, title }))}
                  error={fieldErrors['title.uz']}
                />
                <TranslatableInput
                  label="Qisqa mazmun"
                  multiline
                  rows={3}
                  value={form.excerpt}
                  onChange={(excerpt) => setForm((current) => ({ ...current, excerpt }))}
                  hint="Ro‘yxatdagi kartochkada ko‘rinadi"
                />
                <RichTextInput
                  label="To‘liq matn"
                  value={form.body}
                  onChange={(body) => setForm((current) => ({ ...current, body }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Video" />
              <CardBody>
                <Input
                  label="YouTube havolasi"
                  value={form.youtubeUrl}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, youtubeUrl: event.target.value }))
                  }
                  placeholder="https://www.youtube.com/watch?v=..."
                  hint="Video yangilik matni ostida ochiladi. Bo‘sh qoldirilsa chizilmaydi"
                  error={fieldErrors.youtubeUrl}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Galereya" />
              <CardBody>
                <MediaGalleryPicker
                  label="Qo‘shimcha rasmlar"
                  value={form.gallery}
                  onChange={(gallery) => setForm((current) => ({ ...current, gallery }))}
                />
              </CardBody>
            </Card>

            <SeoPanel
              value={form.seo}
              onChange={(seo) => setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.title.uz || undefined}
            />
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Sozlamalar" />
              <CardBody className="space-y-4">
                <MediaPicker
                  label="Muqova rasmi"
                  value={form.coverImage}
                  onChange={(coverImage) => setForm((current) => ({ ...current, coverImage }))}
                />
                <Select
                  label="Kategoriya"
                  value={form.categoryId}
                  placeholder="Tanlanmagan"
                  options={categories}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, categoryId: event.target.value }))
                  }
                />
                <Input
                  label="Teglar"
                  value={form.tags}
                  placeholder="dron, aerofototasvir"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, tags: event.target.value }))
                  }
                  hint="Vergul bilan ajrating"
                />
                <Input
                  label="Manzil (slug)"
                  value={form.slug}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, slug: event.target.value }))
                  }
                  error={fieldErrors.slug}
                  hint="Bo‘sh qoldirilsa sarlavhadan yasaladi"
                />
                <Checkbox
                  label="Tanlangan yangilik"
                  hint="Bosh sahifadagi «tanlanganlar» rejimida chiqadi"
                  checked={form.isFeatured}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isFeatured: event.target.checked }))
                  }
                />
              </CardBody>
            </Card>
          </div>
        </div>
      )}
    />
  );
}

// ─── Kategoriyalar ───────────────────────────────────────────────────────────

export function NewsCategoriesListPage() {
  return (
    <ContentListPage<NewsCategoryRecord>
      title="Yangilik kategoriyalari"
      collection="news-categories"
      contentApi={newsCategoriesApi}
      basePath="/news-categories"
      createLabel="Kategoriya qo‘shish"
      sortable
      emptyTitle="Hozircha kategoriya yo‘q"
      columns={[
        {
          key: 'name',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.name?.uz || '(nomsiz)'}</span>
          ),
        },
      ]}
    />
  );
}

type CategoryForm = { name: TranslatableString; slug: string };

export function NewsCategoryEditPage() {
  return (
    <ContentEditPage<CategoryForm>
      collection="news-categories"
      contentApi={newsCategoriesApi}
      basePath="/news-categories"
      backLabel="Kategoriyalar"
      newTitle="Yangi kategoriya"
      previewable={false}
      titleOf={(form) => form.name.uz}
      empty={() => ({ name: { uz: '', ru: '', en: '' }, slug: '' })}
      fromRecord={(record) => ({
        name: (record as NewsCategoryRecord).name ?? { uz: '', ru: '', en: '' },
        slug: record.slug ?? '',
      })}
      toInput={(form) => ({ name: form.name, slug: form.slug })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <Card className="max-w-2xl">
          <CardHeader title="Kategoriya" />
          <CardBody className="space-y-4">
            <TranslatableInput
              label="Nomi"
              required
              value={form.name}
              onChange={(name) => setForm((current) => ({ ...current, name }))}
              error={fieldErrors['name.uz']}
            />
            <Input
              label="Manzil (slug)"
              value={form.slug}
              onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
              error={fieldErrors.slug}
              hint="Bo‘sh qoldirilsa nomdan yasaladi"
            />
          </CardBody>
        </Card>
      )}
    />
  );
}
